// Core React imports
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Third-party libraries
import { Calendar, CheckCircle, Shield, Video } from "react-feather";

// Application imports
import { MediaRecorderConfig, SessionMode } from "../../libs/constants/index";
import useAppStore from "../../store/AppStore";
import { useBoundStore } from "../../store/UseBoundStore1";
import useLiveAvatarStore from "../../store/LiveAvatarStore";
import rfetch from "../../utils/network/recursive-fetch";
import * as Sentry from "../../config/sentry";
import { siteVersion } from "../../config/version";

// Assets and styles
import logo from "../../assests/images/logo.png";
import "../../styles/LoginFormNew.css";

const VideoInterviewLogin = ({
  userData,
  interviewData,
  isBrowserSupported,
  LinkError,
}) => {
  const navigate = useNavigate();
  const { setAccessToken } = useAppStore();

  const {
    // Read-only state
    mId,
    is2D,
    version,
    firstName,
    mode,
    organizationName,
    jobTitle,
    organizationContactEmail,
    organizationContactNumber,
    organizationContactCountryCode,
    // Functions
    handleLogin,
    setVideoAvatarId,
    setVideoVoiceId,
    setAudioVoiceId,
    setUltravoxJoinUrl,
  } = useBoundStore();

  const { setSysPrompt, setOpener } = useLiveAvatarStore();

  // Component state
  const [preInterviewChecklist, setPreInterviewChecklist] = useState({
    environment: false,
    internetConnection: false,
    aiResponseTime: false,
    browserPermissions: false,
  });

  const [isLoading, setIsLoading] = useState({
    joinInterviewApi: false,
  });

  const handleSubmit = async (event) => {
    setIsLoading((e) => ({ ...e, joinInterviewApi: true }));

    if (mId === "") {
      console.log("[VideoInterviewLogin] No meetingId found:", { mId });
      return;
    }

    if (userData?.username === "") {
      console.log("[VideoInterviewLogin] No username found:", {
        username: userData?.username,
      });
      return;
    }
    try {
      const response = await rfetch(
        `${process.env.REACT_APP_BASE_URL}/join_meeting/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            meeting_id: mId,
            user_agent: navigator.userAgent,
            candidate_first_name: userData?.candidateFirstName,
            candidate_last_name: userData?.candidateLastName,
            candidate_phone_number: userData?.toPhoneNumber,
            job_title: userData?.candidateJobTitle,
            version: version,
            ext: MediaRecorderConfig.extension,
            media_type: "video",
          }),
        },
        {
          timeout: 5000,
          maxRetries: 10,
        }
      );
      if (response.ok) {
        const token = await response.json();
        console.log("[VideoInterviewLogin] Join meeting successful:", {
          hasAccessToken: !!token.access_token,
          hasVideoAvatarId: !!token.pref_obj?.video_avatar_id,
          hasVideoVoiceId: !!token.pref_obj?.video_voice_id,
          hasAudioVoiceId: !!token.pref_obj?.audio_voice_id,
          hasUltravoxJoinUrl: !!token?.ultravox_join_url,
          hasSysPrompt: !!token?.sys_prompt,
          hasOpener: !!token?.opener,
          mId,
          version,
        });
        setAccessToken(token.access_token);

        document.cookie = `jwt=${token.access_token}; path=/; SameSite=Strict`;
        if (token.pref_obj?.video_avatar_id)
          setVideoAvatarId(token.pref_obj?.video_avatar_id);
        if (token.pref_obj?.video_voice_id)
          setVideoVoiceId(token.pref_obj?.video_voice_id);
        if (token.pref_obj?.audio_voice_id)
          setAudioVoiceId(token.pref_obj?.audio_voice_id);
        if (token?.ultravox_join_url)
          setUltravoxJoinUrl(token?.ultravox_join_url);
        if (token?.sys_prompt) setSysPrompt(token?.sys_prompt);
        if (token?.opener) setOpener(token?.opener);
        handleLogin(
          true,
          userData?.username ?? "username",
          mId ?? "mid",
          token.mstatus ?? "meetingStatus",
          interviewData?.isOldUI ?? false,
          is2D ?? false,
          version ?? "v3",
          mode ?? SessionMode.WEB_VIDEO,
          firstName,
          jobTitle,
          organizationName,
          organizationContactEmail,
          organizationContactNumber,
          organizationContactCountryCode
        );

        navigate("/botUserInteraction");
      } else {
        const data = await response.json();
        console.error("[VideoInterviewLogin] Join meeting failed:", {
          status: response.status,
          statusText: response.statusText,
          data,
          mId,
          username: userData?.username,
        });
      }
    } catch (error) {
      console.error("[VideoInterviewLogin] Error during login process:", {
        error: error.message,
        stack: error.stack,
        mId,
        username: userData?.username,
        version,
      });
      Sentry.captureException(error);
    } finally {
      setIsLoading((e) => ({ ...e, joinInterviewApi: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] flex justify-center items-center">
      <div className="flex flex-col items-center justify-center w-full">
        {/* <!-- Logo --> */}
        <div className="w-48 md:w-56 mb-4">
          <img src={logo} alt="Logo" />
        </div>

        <div className="w-full max-w-3xl bg-white rounded-xl shadow-md p-6">
          <h1 className="text-2xl font-semibold text-center bg-gradient-to-r from-[#4776e6] to-[#8E54E9] text-transparent bg-clip-text">
            Welcome to your interview session
            {firstName ? `, ${firstName}!` : ""}
          </h1>
          <p className="hidden md:flex w-full justify-center text-center text-xs text-gray-500 mt-2">
            Please complete the preparation steps below to ensure the best
            interview experience
          </p>

          {/* Stepper */}
          <div className="flex flex-wrap items-center justify-center mt-6 gap-x-3 gap-y-2">
            <div className="flex flex-col items-center text-app-primary">
              <div className="text-sm w-7 h-7 border border-app-primary text-app-primary rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <p className="text-[13px] mt-1 font-medium">Pre-Interview</p>
              <p className="text-[10px] text-gray-400">Complete checklist</p>
            </div>

            <div className="border-t h-[1px] w-10 sm:w-16"></div>

            <div className="flex flex-col items-center text-gray-400">
              <div className="text-sm w-7 h-7 border border-gray-300 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <p className="text-[13px] mt-1 font-medium">Device Setup</p>
              <p className="text-[10px] text-gray-400">Grant permissions</p>
            </div>

            <div className="border-t h-[1px] w-10 sm:w-16"></div>

            <div className="flex flex-col items-center text-gray-400">
              <div className="text-sm w-7 h-7 border border-gray-300 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <p className="text-[13px] mt-1 font-medium">Mic Check</p>
              <p className="text-[10px] text-gray-400">Test your microphone</p>
            </div>
          </div>

          {/* Interview Details */}
          <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-lg p-4 mt-6">
            <div className="mb-4 flex items-center gap-x-2">
              <Calendar size={16} className="text-app-primary" />
              <p className="text-[#101828] text-sm font-medium">
                Interview Details
              </p>
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px] font-medium text-gray-600`}
            >
              {userData?.username && (
                <>
                  <div>
                    <p className="text-gray-400 font-normal">Role</p>
                    <p className="text-[#101828] text-xs font-medium">
                      {jobTitle}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-normal">Organization</p>
                    <p className="text-[#101828] text-xs font-medium">
                      {organizationName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 font-normal">Interview Type</p>
                    <div className="flex items-center gap-1 text-[#101828] font-semibold gap-x-2">
                      <Video size={14} className="text-app-primary" />
                      <p className="text-[#101828] text-xs font-medium">
                        <span>AI Interview</span>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* {interviewData?.meetingStatus !== "" && (
              <div className="w-full text-sm text-center text-red-600 ms-5 font-medium">
                {interviewData?.meetingStatus}
              </div>
            )} */}
            {/* {!isBrowserSupported && (
              <div className="w-full text-sm text-center text-red-600 my-3 font-medium">
                Due to limited functionality in this browser, we recommend to
                use different browser.
              </div>
            )} */}
            {LinkError ? (
              <div className="w-full text-sm text-center text-red-500 my-3 font-medium">
                {LinkError}
              </div>
            ) : (
              !LinkError && !isBrowserSupported && (
                <div className="w-full text-base text-center text-red-500 my-3 font-semibold">
                  Browser Not Supported - Please use Chrome or Edge for optimal interview experience.
                </div>
              )
            )}
          </div>

          {/* Checklist */}
          {/* {activeStep === 1 ? ( */}
          <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-lg px-3 py-4 mt-6">
            <div className="flex items-center justify-start mb-4 gap-x-2">
              <CheckCircle size={16} className="text-app-primary" />
              <h5 className="text-app-primary text-sm font-semibold ">
                Pre-Interview Checklist
              </h5>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="w-full flex items-start gap-2">
                <div className="w-[8%] flex items-center justify-center">
                  <input
                    type="checkbox"
                    value={preInterviewChecklist.environment}
                    onChange={(event) =>
                      setPreInterviewChecklist((e) => ({
                        ...e,
                        environment: event.target.checked,
                      }))
                    }
                    className={`mt-1 accent-app-primary w-4 h-4  ${
                      !isBrowserSupported || LinkError
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    disabled={!isBrowserSupported || LinkError}
                  />
                </div>
                <div className="w-[90%]">
                  <p className="font-medium text-[13px] text-app-text">
                    Environment
                  </p>
                  <p className="text-gray-500">
                    Find a quiet, well-lit room with a neutral background,
                    minimal distractions, and minimal background noise.
                  </p>
                </div>
              </div>
              <div className="w-full flex items-start gap-2">
                <div className="w-[8%] flex items-center justify-center">
                  <input
                    type="checkbox"
                    value={preInterviewChecklist.internetConnection}
                    onChange={(event) =>
                      setPreInterviewChecklist((e) => ({
                        ...e,
                        internetConnection: event.target.checked,
                      }))
                    }
                    className={`mt-1 accent-app-primary w-4 h-4 ${
                      !isBrowserSupported || LinkError
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    disabled={!isBrowserSupported || LinkError}
                  />
                </div>
                <div className="w-[90%]">
                  <p className="font-medium text-[13px] text-app-text">
                    Internet Connection
                  </p>
                  <p className="text-gray-500">
                    Ensure you have a stable internet connection with sufficient
                    speed for a smooth experience
                  </p>
                </div>
              </div>

              <div className="w-full flex items-start gap-2">
                <div className="w-[8%] flex items-center justify-center">
                  <input
                    type="checkbox"
                    value={preInterviewChecklist.browserPermissions}
                    onChange={(event) =>
                      setPreInterviewChecklist((e) => ({
                        ...e,
                        browserPermissions: event.target.checked,
                      }))
                    }
                    className={`mt-1 accent-app-primary w-4 h-4 ${
                      !isBrowserSupported || LinkError
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    disabled={!isBrowserSupported || LinkError}
                  />
                </div>
                <div className="w-[90%]">
                  <p className="font-medium text-[13px] text-app-text">
                    Browser Permissions
                  </p>
                  <p className="text-gray-500">
                    Be prepared to allow camera, microphone, and screen sharing
                    permissions when prompted
                  </p>
                </div>
              </div>

              <div className="w-full flex items-start gap-2">
                <div className="w-[8%] flex items-center justify-center">
                  <input
                    type="checkbox"
                    value={preInterviewChecklist.aiResponseTime}
                    onChange={(event) => {
                      setPreInterviewChecklist((e) => ({
                        ...e,
                        aiResponseTime: event.target.checked,
                      }));
                    }}
                    className={`mt-1 accent-app-primary w-4 h-4 ${
                      !isBrowserSupported || LinkError
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    disabled={!isBrowserSupported || LinkError}
                  />
                </div>
                <div className="w-[90%]">
                  <p className="font-medium text-[13px] text-app-text">
                    AI Response Time
                  </p>
                  <p className="text-gray-500">
                    AI interviewer make require 2-3 secs to respond back to your
                    answer.
                  </p>
                </div>
              </div>
            </div>

            {/* Important Notice - Do not refresh page during interview */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-start gap-x-3 text-sm px-4 py-3 rounded-md border ">
              <div>
                <p className="text-[12px] font-semibold text-gray-900">
                  Note: Do Not Refresh This Page
                </p>
                <p className="text-[10px] mt-1 text-gray-800">
                  Once your interview begins, please do not refresh or close
                  this browser tab as it will disconnect your session and may
                  affect your interview.
                </p>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          {isBrowserSupported && (
            <div className="mt-8 flex justify-center md:justify-end">
              <button
                className={`text-sm  font-semibold px-5 py-3 rounded-md w-56 ${
                  userData?.username &&
                  userData?.email &&
                  preInterviewChecklist.aiResponseTime &&
                  preInterviewChecklist.browserPermissions &&
                  preInterviewChecklist.environment &&
                  preInterviewChecklist.internetConnection
                    ? "bg-app-primary text-white hover:bg-app-primaryHover2 cursor-pointer"
                    : "bg-app-slateGray text-app-darkGrayText cursor-not-allowed hover:bg-app-slateGray"
                }`}
                onClick={
                  userData?.username &&
                  userData?.email &&
                  preInterviewChecklist.aiResponseTime &&
                  preInterviewChecklist.browserPermissions &&
                  preInterviewChecklist.environment &&
                  preInterviewChecklist.internetConnection
                    ? (e) => handleSubmit(e)
                    : () => {}
                }
              >
                {isLoading.joinInterviewApi ? (
                  <span className="loading loading-dots loading-sm text-white"></span>
                ) : (
                  "Continue to Device Setup"
                )}
              </button>
            </div>
          )}

          {/* --------------------  Note ------------------------*/}
          {preInterviewChecklist.aiResponseTime &&
          preInterviewChecklist.environment &&
          preInterviewChecklist.internetConnection &&
          preInterviewChecklist.browserPermissions ? (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-start gap-x-3 bg-app-successLight text-app-success text-sm px-4 py-3 rounded-md border border-app-successLight">
              <Shield size={19} />
              <div>
                <p className="text-[13px] font-medium">
                  You're all set to join your interview
                </p>
                <p className="text-[10px]">
                  For the best experience, we recommend completing all
                  preparation steps.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex gap-y-2  flex-col sm:flex-row items-center justify-center sm:justify-start gap-x-3 bg-app-secondaryLight text-app-secondary text-sm px-4 py-3 rounded-md border border-app-secondaryLight">
              <Shield size={19} />
              <div>
                <p className="text-[13px] font-medium text-center sm:text-start">
                  {!LinkError && !isBrowserSupported
                    ? "Browser Not Supported"
                    : "Complete the checklist before proceeding"}
                </p>
                <p className="text-[12px] text-center sm:text-start">
                  {!LinkError && !isBrowserSupported
                    ? "Please use Chrome or Edge browser for the best interview experience."
                    : "For the best experience, we recommend completing all preparation steps."}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}

          <p className="text-center text-gray-400 font-normal text-xs mt-4">
            {siteVersion}
          </p>
          <p className="text-center text-gray-400 font-normal text-xs mt-2">
            © 2025 SofiaWorks. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoInterviewLogin;
